import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';
import Badge from './Badge.jsx';
import MODULE_BADGES from '../lib/moduleBadges.js';

// Per-site subscription editor. Renders the same 6 modules + 7 add-ons
// as the legacy customer-level SubscriptionsTab, but PUTs to the
// site-keyed endpoint so each site can carry different modules and
// renewal dates. Lives inside the Sites tab's expanded row.
//
// The standalone customer-level "Send Price Increase Email" button is
// intentionally NOT here — that flow still aggregates per-customer and
// lives elsewhere (we surface it via a button at the SitesTab header
// level so it isn't repeated per row).

// All 13 subscription modules — the original 6 plus the 7 promoted
// from add-ons. Each carries the same 4 fields (annual, renewal,
// pct, next). The standalone add-on grid is gone.
const MODULES = [
  { key: 'live_transactions', label: 'Real Time Inventory' },
  { key: 'price_book',        label: 'Price Book'          },
  { key: 'fuel_manager',      label: 'Fuel Manager'        },
  { key: 'charge',            label: 'Charge Accounts'     },
  { key: 'tx_exempt',         label: 'Tax Exempt'          },
  { key: 'car_wash',          label: 'Car Wash'            },
  { key: 'adv_report',        label: 'Financial Report'  },
  { key: 'fuel_price',        label: 'Fuel Price Changes'  },
  { key: 'case_break',        label: 'Case Breakdown'      },
  { key: 'dealer_comm',       label: 'Dealer Commission'   },
  { key: 'time_rest',         label: 'Time Restrictions'   },
  { key: 'sft_file',          label: 'SFT File'            },
  { key: 'private_label',     label: 'Private Label'       },
  // ── Mission Control alignment additions ────────────────────
  { key: 'imperial_import',   label: 'Imperial Import'     },
  { key: 'fuel_grade_report', label: 'Fuel Grade Report'   },
  { key: 'inventory_control', label: 'Inventory Control'   }
];

function cols(moduleKey) {
  return {
    price:   `${moduleKey}_annual`,
    renewal: `${moduleKey}_renewal_date`,
    pct:     `${moduleKey}_pct_increase`,
    next:    `${moduleKey}_next_price`
  };
}

const RENEWAL_FIELDS = MODULES.map((m) => cols(m.key).renewal);

const EMPTY = (() => {
  const o = {};
  for (const m of MODULES) {
    const c = cols(m.key);
    o[c.price] = ''; o[c.renewal] = ''; o[c.pct] = ''; o[c.next] = '';
  }
  return o;
})();

function fromServer(sub) {
  const out = { ...EMPTY };
  if (!sub) return out;
  for (const m of MODULES) {
    const c = cols(m.key);
    out[c.price]   = sub[c.price]   != null ? String(sub[c.price])   : '';
    out[c.renewal] = sub[c.renewal] ? String(sub[c.renewal]).slice(0, 10) : '';
    out[c.pct]     = sub[c.pct]     != null ? String(sub[c.pct])     : '';
    out[c.next]    = sub[c.next]    != null ? String(sub[c.next])    : '';
  }
  return out;
}

function moneyFmt(value) {
  if (value === '' || value == null) return '';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function deriveStatus(form) {
  const dates = RENEWAL_FIELDS
    .map((f) => form[f])
    .filter(Boolean)
    .map((d) => new Date(d + 'T00:00:00'))
    .filter((d) => !Number.isNaN(d.getTime()));
  if (dates.length === 0) return '';
  const earliest = new Date(Math.min(...dates.map((d) => d.getTime())));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((earliest - today) / (1000 * 60 * 60 * 24));
  if (days < 0)   return 'Overdue';
  if (days <= 180) return 'Near';
  return 'Active';
}

function n(v) {
  const p = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(p) ? p : 0;
}

function computeNextPrice(annual, pct) {
  const a = n(annual);
  if (a === 0) return '';
  const factor = 1 + n(pct) / 100;
  return Math.round(a * factor * 100) / 100;
}

function deriveCurrentTotal(form) {
  let total = 0;
  for (const m of MODULES) total += n(form[cols(m.key).price]);
  return Math.round(total * 100) / 100;
}

function deriveNextYearTotal(form) {
  let total = 0;
  for (const m of MODULES) {
    const c = cols(m.key);
    const next = computeNextPrice(form[c.price], form[c.pct]);
    if (next !== '') total += next;
  }
  return Math.round(total * 100) / 100;
}

function deriveLtv(form) {
  return Math.round(deriveCurrentTotal(form) * 5 * 100) / 100;
}

export default function SiteSubscriptionPanel({ siteId, subscription, onSaved }) {
  const [form, setForm] = useState(() => fromServer(subscription));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMessage, setSavedMessage] = useState(null);

  // Renewal confirmation dialog state. Opens when the user clicks the
  // "Mark Renewed (+1y)" button next to the Renewal Status chip; closes
  // on Cancel or after a successful renew. Keeps the user-visible
  // banner about which modules rolled and the new earliest renewal
  // date so they get immediate feedback without scrolling around.
  const [renewOpen,    setRenewOpen]    = useState(false);
  const [renewBusy,    setRenewBusy]    = useState(false);
  const [renewMessage, setRenewMessage] = useState(null);

  // Separate state for the Update xsiteio button -- decoupled from
  // Mark Renewed (Dave 2026-06-09) so a failed xsiteio sync can be
  // retried without re-rolling local renewal dates.
  const [xsBusy,    setXsBusy]    = useState(false);
  const [xsMessage, setXsMessage] = useState(null);

  // Pull from xsiteio button state. Mirrors Update xsiteio but in the
  // opposite direction -- fetches the site's current billing plan
  // state from xsiteio and writes it into our local subscriptions row.
  // Use case: modules toggled on in xsiteio AFTER the wizard's first
  // pull don't otherwise land in Command until someone re-runs the
  // wizard. With this button it's one click on the panel itself.
  const [pullBusy,    setPullBusy]    = useState(false);
  const [pullMessage, setPullMessage] = useState(null);

  // Apply Queued Increase state (Dave 2026-06-11): promotes Next ->
  // Annual + clears % INC for every module that has a queued price
  // increase. Used when the renewal date has already been rolled but
  // the price didn't update (legacy markRenewed behaviour). After
  // clicking, the user normally hits Update xsiteio so the new annual
  // gets pushed to xsiteio's billing system too.
  const [aqiBusy,    setAqiBusy]    = useState(false);
  const [aqiMessage, setAqiMessage] = useState(null);

  useEffect(() => {
    setForm(fromServer(subscription));
  }, [subscription, siteId]);

  const liveStatus       = useMemo(() => deriveStatus(form),         [form]);
  const liveCurrentTotal = useMemo(() => deriveCurrentTotal(form),   [form]);
  const liveNextTotal    = useMemo(() => deriveNextYearTotal(form),  [form]);
  const liveLtv          = useMemo(() => deriveLtv(form),            [form]);

  function update(field) {
    return (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  // Modules whose renewal_date is within 60 days (matches the server-
  // side mark-renewed window). These are the rows that will actually
  // roll forward when the user confirms -- we surface this in the
  // dialog so they can sanity-check before committing.
  const renewableModules = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() + 60);
    const out = [];
    for (const m of MODULES) {
      const c = cols(m.key);
      const raw = form[c.renewal];
      if (!raw) continue;
      const d = new Date(raw + 'T00:00:00');
      if (Number.isNaN(d.getTime())) continue;
      if (d <= cutoff) {
        const advanced = new Date(d); advanced.setFullYear(advanced.getFullYear() + 1);
        out.push({
          key:      m.key,
          label:    m.label,
          annual:   form[c.price],
          from:     raw,
          to:       advanced.toISOString().slice(0, 10)
        });
      }
    }
    return out;
  }, [form]);

  async function handleMarkRenewed() {
    setRenewBusy(true); setError(null); setRenewMessage(null);
    try {
      const r = await axios.post('/api/subscriptions/mark-renewed', { site_id: siteId });
      const after = r.data?.modules_after || {};
      const moved = renewableModules.filter((m) => after[m.key]);
      const label = moved.length === 1
        ? `${moved[0].label} rolled to ${after[moved[0].key]}`
        : `${moved.length} modules rolled forward 1 year`;
      setRenewMessage(label);
      setRenewOpen(false);
      if (onSaved) onSaved();   // parent reloads the subscription row
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRenewBusy(false);
    }
  }

  // Pull the site's CURRENT billing plan state from xsiteio into our
  // local subscriptions row. Same backend the wizard's Step 4 Pull
  // button uses (POST /api/sites/:id/xsiteio/pull) so any module that
  // was activated on xsiteio after onboarding can be picked up with
  // one click. Doesn't overwrite Command-only modules (SFT File etc.)
  // because the controller's MODULE_PLAN_IDS only maps the xsiteio
  // synced ones.
  async function handlePullFromXsiteio() {
    setPullBusy(true); setError(null); setPullMessage(null);
    try {
      // siteId here is the LOCAL site id (Command's sites.id), which is
      // what the route expects -- not the xsiteio_site_id.
      const r = await axios.post(`/api/sites/${siteId}/xsiteio/pull`);
      const changes = r.data?.changes?.subscription_modules || {};
      const moduleCount = Object.keys(changes).length;
      const label = moduleCount === 0
        ? 'Pulled from xsiteio — no module changes detected'
        : `Pulled ${moduleCount} module${moduleCount === 1 ? '' : 's'} from xsiteio`;
      setPullMessage(label);
      if (onSaved) onSaved();   // parent reloads the subscription row
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPullBusy(false);
    }
  }

  // Promote Next -> Annual + clear % INC for every module that has a
  // queued price increase. Used to catch up sites whose renewal_date
  // was rolled before the markRenewed price-promotion fix landed. UI
  // shows a banner with the per-module before/after, so the user can
  // verify the right modules rolled before clicking Update xsiteio.
  async function handleApplyQueuedIncrease() {
    setAqiBusy(true); setError(null); setAqiMessage(null);
    try {
      const r = await axios.post('/api/subscriptions/apply-queued-increase', { site_id: siteId });
      const rolled = r.data?.rolled || [];
      let label;
      if (rolled.length === 0) {
        label = 'No queued price increases to apply';
      } else if (rolled.length === 1) {
        const m = rolled[0];
        label = `${m.label}: $${m.old_annual.toFixed(2)} -> $${m.new_annual.toFixed(2)} (+${m.pct}%)`;
      } else {
        label = `Applied increase on ${rolled.length} module${rolled.length === 1 ? '' : 's'}: ` +
          rolled.map((m) => `${m.label} $${m.old_annual.toFixed(0)}->$${m.new_annual.toFixed(0)}`).join(', ');
      }
      setAqiMessage(label);
      if (onSaved) onSaved();   // parent reloads the subscription row
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAqiBusy(false);
    }
  }

  // Push the current local subscription state up to xsiteio. Standalone
  // action: doesn't change local DB, just mirrors what's already there
  // to xsiteio's billing. Used as a retry after Mark Renewed succeeded
  // locally but xsiteio rejected the sync, or after manual edits to the
  // grid below.
  async function handlePushToXsiteio() {
    setXsBusy(true); setError(null); setXsMessage(null);
    try {
      const r = await axios.post('/api/subscriptions/push-to-xsiteio', { site_id: siteId });
      const xs = r.data?.xsiteio_sync;
      let label;
      if (!xs) {
        label = 'xsiteio sync completed (no detail returned)';
      } else if (xs.skipped === true) {
        label = 'xsiteio sync skipped (' + (xs.reason || 'unknown') + ')';
      } else if (xs.error) {
        label = 'xsiteio sync FAILED: ' + xs.error;
      } else {
        const ok   = xs.synced?.length  || 0;
        const bad  = xs.failed?.length  || 0;
        const skip = xs.skipped?.length || 0;
        label = `xsiteio: ${ok} synced` +
          (bad  ? `, ${bad} failed`   : '') +
          (skip ? `, ${skip} skipped` : '');
        if (bad && xs.failed?.[0]) {
          const f = xs.failed[0];
          label += ` (first: ${f.module} ${f.error || ''}${f.body ? ' — ' + f.body : ''})`;
        }
      }
      setXsMessage(label);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setXsBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const payload = { ...form };
      // Activeness is driven entirely by renewal_date being set —
      // no separate `_active` flag is written.
      for (const m of MODULES) {
        const c = cols(m.key);
        const next = computeNextPrice(payload[c.price], payload[c.pct]);
        payload[c.next] = next === '' ? null : next;
      }
      for (const k of Object.keys(payload)) {
        if (typeof payload[k] === 'string' && payload[k] === '') payload[k] = null;
      }
      await axios.put(`/api/subscriptions/site/${siteId}`, payload);
      setSavedMessage('Saved.');
      if (onSaved) onSaved();
      setTimeout(() => setSavedMessage(null), 2500);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="subscription-form site-subscription-form" onSubmit={handleSubmit}>
      <div className="subscription-section-head">
        <h4 className="preview-heading" style={{ marginTop: 0 }}>Subscription Modules</h4>
        {/* Action group — chip + 4 buttons. Wraps to a second row
            when the panel is too narrow so the rightmost button
            (Apply Price Increase / Update xsiteio) never gets clipped
            off the right edge of the panel (Dave 2026-06-11). */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          rowGap: 'var(--space-2)',
          // Take full width of the section-head so the action group
          // always lives on its own row below the title — buttons
          // then render left-to-right with plenty of room and never
          // get clipped on the right edge (Dave 2026-06-11).
          flexBasis: '100%'
        }}>
          {liveStatus && (
            <span className="subscription-status-chip">
              <span className="subscription-status-chip-label">Renewal Status</span>
              <span className={`badge badge-${liveStatus === 'Overdue' ? 'red' : liveStatus === 'Near' ? 'amber' : 'green'}`}>
                {liveStatus}
              </span>
            </span>
          )}
          {/* Inline Mark Renewed action -- mirrors the per-row button on
              the Renewals tab, but lives where the user is already
              looking at the renewal date. Disabled when nothing is due
              within 60 days so the button doesn't pretend to do work
              it isn't going to do. */}
          {/* Pull from xsiteio -- fetches the site's current billing
              plan state and writes it into the local row. Use when a
              module was toggled on in xsiteio after onboarding and
              hasn't shown up in Command yet. */}
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={(e) => { e.preventDefault(); handlePullFromXsiteio(); }}
            disabled={pullBusy}
            title="Pull the latest billing plan state from xsiteio into the panel below"
          >
            {pullBusy ? 'Pulling…' : 'Pull from xsiteio'}
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={(e) => { e.preventDefault(); setRenewOpen(true); }}
            disabled={renewableModules.length === 0 || renewBusy}
            title={renewableModules.length === 0
              ? 'No modules are due to renew within 60 days'
              : 'Roll the renewal date forward one year (requires verification)'}
          >
            Mark Renewed (+1y)
          </button>
          {/* Apply Queued Increase -- promotes Next -> Annual + clears
              % INC for modules with a queued price increase. Useful
              after the customer has paid the new (higher) renewal price
              but the local Annual still shows the old amount. */}
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={(e) => { e.preventDefault(); handleApplyQueuedIncrease(); }}
            disabled={aqiBusy}
            title="Promote Next -> Annual and clear % INC for any module with a queued price increase"
          >
            {aqiBusy ? 'Applying…' : 'Apply Price Increase'}
          </button>
          {/* Update xsiteio -- pushes current local state up to xsiteio
              billing. Separate from Mark Renewed so it can be retried
              independently when xsiteio rejects an initial sync. */}
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={(e) => { e.preventDefault(); handlePushToXsiteio(); }}
            disabled={xsBusy}
            title="Push the current local renewal dates and prices up to xsiteio"
          >
            {xsBusy ? 'Updating…' : 'Update xsiteio'}
          </button>
        </div>
      </div>
      {pullMessage && (
        <p className="save-message" style={{ marginTop: 0 }}>{pullMessage}</p>
      )}
      {renewMessage && (
        <p className="save-message" style={{ marginTop: 0 }}>{renewMessage}</p>
      )}
      {aqiMessage && (
        <p className="save-message" style={{ marginTop: 0 }}>{aqiMessage}</p>
      )}
      {xsMessage && (
        <p className="save-message" style={{ marginTop: 0 }}>{xsMessage}</p>
      )}

      <div className="subscription-scroll">
      <div className="subscription-grid" role="table" aria-label="Subscription pricing">
        <div className="subscription-grid-head" role="row">
          <div role="columnheader">Module</div>
          <div role="columnheader">Annual</div>
          <div role="columnheader">Renewal</div>
          <div role="columnheader">% Inc</div>
          <div role="columnheader">Next</div>
        </div>

        {MODULES.map((m) => {
          const c = cols(m.key);
          const badge = MODULE_BADGES[m.key];
          return (
            <div key={m.key} className="subscription-grid-row" role="row">
              <div className="subscription-grid-label" role="cell">
                {badge && <Badge tone={badge.tone}>{badge.abbr}</Badge>}
                <span className="subscription-grid-label-text">{m.label}</span>
              </div>
              <div role="cell">
                <input
                  type="number" step="0.01"
                  className="cell-mono subscription-input subscription-input-price"
                  value={form[c.price]} onChange={update(c.price)}
                  aria-label={`${m.label} annual price`}
                />
              </div>
              <div role="cell">
                <DateInput
                  className="cell-mono subscription-input subscription-input-date"
                  value={form[c.renewal]} onChange={update(c.renewal)}
                  aria-label={`${m.label} renewal date`}
                />
              </div>
              <div role="cell">
                <input
                  type="number" step="0.01"
                  className="cell-mono subscription-input subscription-input-pct"
                  value={form[c.pct]} onChange={update(c.pct)}
                  aria-label={`${m.label} percent increase`}
                />
              </div>
              <div role="cell"
                   className="subscription-computed cell-mono"
                   aria-label={`${m.label} next price (calculated)`}
                   title="Auto-calculated from Annual × (1 + % Increase). Only shown when % INC > 0.">
                {(() => {
                  // Only show Next when there's an actual queued increase.
                  // Otherwise annual = next (pct=0), which the previous
                  // version confusingly displayed as a real-looking value
                  // -- Dave hit this 2026-07-09 thinking Apply Price
                  // Increase should re-fire on a $1,920 Next when pct
                  // was already 0.
                  const pctNum = n(form[c.pct]);
                  if (!(pctNum > 0)) return '—';
                  const next = computeNextPrice(form[c.price], form[c.pct]);
                  return next === '' ? '—' : moneyFmt(next);
                })()}
              </div>
            </div>
          );
        })}
      </div>

      <div className="subscription-totals" aria-label="Subscription totals">
        <div className="subscription-totals-item">
          <span className="subscription-totals-label">Annual</span>
          <span className="subscription-totals-value cell-mono">{moneyFmt(liveCurrentTotal)}</span>
        </div>
        <div className="subscription-totals-item">
          <span className="subscription-totals-label">Next Year</span>
          <span className="subscription-totals-value cell-mono">{moneyFmt(liveNextTotal)}</span>
        </div>
        <div className="subscription-totals-item subscription-totals-prominent">
          <span className="subscription-totals-label">5Y LTV</span>
          <span className="subscription-totals-value cell-mono">{moneyFmt(liveLtv)}</span>
        </div>
      </div>

      {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}
      {savedMessage && <p className="save-message">{savedMessage}</p>}
      </div>

      <div className="subscription-footer">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Subscription'}
        </button>
      </div>

      {/* Mark Renewed confirmation dialog. Lightweight inline modal --
          deliberately not a separate component because the only places
          it'd be reused are the customer-side panel (here) and the
          lead-side SubscriptionsTab which doesn't have renewals. The
          QBO verification language is the whole point of this dialog;
          it forces the user to think before they roll a date forward. */}
      {renewOpen && (
        <div
          className="modal-overlay"
          onClick={() => !renewBusy && setRenewOpen(false)}
        >
          <div
            className="modal-content"
            style={{ maxWidth: 520 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Confirm renewal</h3>
            <p style={{ marginTop: 0 }}>
              Have you verified in <strong>QuickBooks</strong> that this payment has been
              received <em>and</em> that the annual amount and current renewal date below
              are accurate?
            </p>
            <p style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
              Confirming will advance the renewal date of every due module
              by exactly one year. Modules not due within 60 days are left
              alone.
            </p>

            <div style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              maxHeight: 200,
              overflowY: 'auto'
            }}>
              {renewableModules.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--color-text-dim)' }}>
                  No modules are due to renew within 60 days.
                </p>
              ) : (
                <table className="cell-mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-small)' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--color-text-dim)' }}>
                      <th style={{ padding: '4px 8px 4px 0' }}>Module</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>Annual</th>
                      <th style={{ padding: '4px 8px' }}>From</th>
                      <th style={{ padding: '4px 0' }}>→ To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renewableModules.map((m) => (
                      <tr key={m.key}>
                        <td style={{ padding: '4px 8px 4px 0' }}>{m.label}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                          {m.annual ? moneyFmt(m.annual) : '—'}
                        </td>
                        <td style={{ padding: '4px 8px' }}>{m.from}</td>
                        <td style={{ padding: '4px 0', fontWeight: 600 }}>{m.to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {error && <p className="error" style={{ marginTop: 0 }}>{error}</p>}

            <div className="form-actions" style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setRenewOpen(false)}
                disabled={renewBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleMarkRenewed}
                disabled={renewBusy || renewableModules.length === 0}
              >
                {renewBusy ? 'Renewing…' : 'Confirm renewal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
